import { useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Checkbox, FormControlLabel, Typography } from '@mui/material';

import { Button } from '@/shared/button/Button';
import { CircularProgress } from '@/shared/circularProgress/CircularProgress';

import type { TClasses } from '@/types/common';
import type { TUserConsentInfo } from '@/types/profile';

import { SF_PRIVACY_POLICY, SF_TERMS_AND_CONDITIONS } from '@/api/urls';

import { useStyles } from './ConsentForm.styles';

type TConsentFormProps = {
  submit: (data: TUserConsentInfo) => void;
  classes?: TClasses<typeof useStyles>;
  captcha?: JSX.Element;
  disabled?: boolean;
  loading?: boolean;
};

export const ConsentForm = ({
  submit,
  captcha,
  disabled,
  loading,
  classes: overrides,
}: TConsentFormProps) => {
  const { t } = useTranslation();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNotifications, setAcceptNotifications] = useState(true);

  const classes = useStyles({ classes: overrides });

  const handleSubmit = () => {
    submit({
      acceptTermsAndConditions: acceptTerms,
      acceptMarketingNotifications: acceptNotifications,
    });
  };

  return (
    <div className={classes.root}>
      <FormControlLabel
        className={classes.point}
        control={
          <Checkbox
            checked={acceptTerms}
            onChange={({ target }) => setAcceptTerms(target.checked)}
            className={classes.pointCheckbox}
            disableRipple
          />
        }
        label={
          <Typography variant="body2">
            <Trans i18nKey="landing.signup.terms">
              <Typography
                component="a"
                variant="subtitle2"
                href={SF_TERMS_AND_CONDITIONS}
                target="_blank"
                rel="noreferrer noopener"
              />
              <Typography
                component="a"
                variant="subtitle2"
                href={SF_PRIVACY_POLICY}
                target="_blank"
                rel="noreferrer noopener"
              />
            </Trans>
          </Typography>
        }
      />

      <FormControlLabel
        className={classes.point}
        control={
          <Checkbox
            checked={acceptNotifications}
            onChange={({ target }) => setAcceptNotifications(target.checked)}
            className={classes.pointCheckbox}
            disableRipple
          />
        }
        label={<Typography variant="subtitle2">{t('landing.signup.marketingInfo')}</Typography>}
      />

      {captcha}

      <Button
        size="large"
        variant="contained"
        onClick={handleSubmit}
        disabled={!acceptTerms || disabled || loading}
        className={classes.submit}
        disableElevation
      >
        <span>{t('landing.signup.continue')}</span>
        {loading && <CircularProgress wrapperClass={classes.submitSpinner} />}
      </Button>
    </div>
  );
};
